<script setup lang="ts">
  import { AuthRole } from '~/utils/auth'
  import type { SupportedLanguage } from '~/composables/useUserPreferences'

  const open = ref(false)
  const props = defineProps<{
    environment: 'portal' | 'management'
  }>()

  const subMenuOpen = ref(false)
  const { locale, t } = useI18n()
  const { savePreferences } = useUserPreferences()
  const toast = useToast()

  const updateLanguage = async (language: SupportedLanguage) => {
    try {
      await savePreferences({ preferredLanguage: language })
      subMenuOpen.value = false
      open.value = false
    } catch {
      toast.error(t('userSettings.preferencesSaveError'))
    }
  }

  const toggleMenu = () => {
    if (open.value) {
      open.value = false
      subMenuOpen.value = false
    } else {
      open.value = true
    }
  }

  const profileMenu = ref<HTMLElement | null>(null)

  useHead({
    link: [
      { rel: 'preload', href: '/flags/en.svg', as: 'image' },
      { rel: 'preload', href: '/flags/nl.svg', as: 'image' },
      { rel: 'preload', href: '/flags/de.svg', as: 'image' },
    ],
  })

  onClickOutside(profileMenu, () => {
    if (open.value) {
      open.value = false
      subMenuOpen.value = false
    }
  })

  const authStore = useAuthStore()
  const showProfileMenu = computed(() => authStore.isUserLoaded)

  const localePath = useLocalePath()
  const { customNavigateTo } = useCustomNavigation()

  const buttonUrl = computed(() =>
    props.environment === 'portal' ? localePath('/hub/dashboard') : localePath('/portal/dashboard'),
  )
</script>

<template>
  <div v-if="showProfileMenu" ref="profileMenu" class="relative">
    <button
      type="button"
      class="relative flex min-h-11 min-w-11 flex-row items-center justify-center cursor-pointer rounded-full focus:ring-4 focus:outline-none focus:ring-focused transition-all duration-250"
      @click.stop="toggleMenu"
    >
      <span
        class="bg-primary w-8 h-8 shrink-0 text-center font-bold mr-0 lg:mr-2 text-sm text-white rounded-full justify-center flex items-center"
        >{{ authStore?.initials }}
      </span>
      <!-- Hide name on smaller screens, show on large screens and up -->
      <span class="hidden lg:flex flex-col text-sm mr-3">
        <span class="text-left text-primary font-bold leading-3">
          {{ authStore?.name }}
        </span>
        <!-- <span class="text-left text-gray-400">GMP+ International</span> -->
      </span>
      <!-- Hide chevron on smaller screens, show on large screens and up -->
      <img
        src="/icons/chevron-down-primary.svg"
        alt=""
        class="hidden w-3 h-3 transition-all duration-300 transform lg:block"
        :class="{
          'rotate-180': open,
          'rotate-0': !open,
        }"
      />
    </button>

    <div
      v-show="open && !subMenuOpen"
      class="absolute mt-2 border right-0 flex flex-col min-w-[200px] divide-y rounded-lg bg-white transition-all animate-in slide-in-from-top-1"
    >
      <AuthGuard :roles="[AuthRole.QRAY_GMP]">
        <ProfileMenuItem
          :label="`nav.profile.switch.${props.environment === 'portal' ? 'management' : 'portal'}`"
          icon="env-24px-black"
          @click="customNavigateTo(buttonUrl)"
          @click.middle="customNavigateTo(buttonUrl, { open: { target: '_blank' } })"
        />
      </AuthGuard>

      <ProfileMenuItem
        label="nav.profile.myProfile"
        icon="ic:outline-person"
        @click="customNavigateTo(localePath('/user'))"
        @click.middle="customNavigateTo(localePath('/user'), { open: { target: '_blank' } })"
      />
      <ProfileMenuItem
        label="nav.profile.language.title"
        icon="language-24px-black"
        @click="subMenuOpen = !subMenuOpen"
      >
        <img src="/icons/chevron-right-24px-black.svg" alt="" class="w-6 h-6 ml-auto" />
      </ProfileMenuItem>
      <ProfileMenuItem
        label="nav.profile.tou"
        icon="ic:outline-assignment"
        @click="customNavigateTo(t('nav.profile.touLink'), { open: { target: '_blank' } })"
      />
      <ProfileMenuItem
        label="nav.profile.website"
        icon="ic:outline-open-in-new"
        @click="customNavigateTo(t('nav.profile.websiteLink'), { open: { target: '_blank' } })"
      />
      <ProfileMenuItem
        label="nav.profile.logout"
        icon="logoff-24px-black"
        @click="authStore.logout()"
      />
    </div>
    <div
      v-if="subMenuOpen"
      class="absolute mt-4 border right-0 flex flex-col w-max min-w-[200px] divide-y rounded-lg bg-white"
    >
      <ProfileMenuItem label="nav.profile.language.title" @click="subMenuOpen = false">
        <template #prefix>
          <img src="/icons/chevron-right-24px-black.svg" alt="" class="w-6 h-6 mr-2 rotate-180" />
        </template>
      </ProfileMenuItem>
      <ProfileMenuItem
        radio
        label="nav.profile.language.en"
        :active="locale == 'en'"
        @click="updateLanguage('en')"
      >
        <template #prefix>
          <img
            src="/flags/en.svg"
            width="24"
            height="24"
            alt="EN"
            class="w-6 h-6 mr-2"
            loading="eager"
          />
        </template>
      </ProfileMenuItem>
      <ProfileMenuItem
        radio
        label="nav.profile.language.nl"
        :active="locale == 'nl'"
        @click="updateLanguage('nl')"
      >
        <template #prefix>
          <img
            src="/flags/nl.svg"
            alt="NL"
            width="24"
            height="24"
            class="w-6 h-6 mr-2"
            loading="eager"
          />
        </template>
      </ProfileMenuItem>
      <ProfileMenuItem
        radio
        label="nav.profile.language.de"
        :active="locale == 'de'"
        @click="updateLanguage('de')"
      >
        <template #prefix>
          <img
            src="/flags/de.svg"
            alt="DE"
            width="24"
            height="24"
            class="w-6 h-6 mr-2"
            loading="eager"
          />
        </template>
      </ProfileMenuItem>
    </div>
  </div>
</template>

<style scoped></style>
